// Pass 3 — /quick-sim
//
// The non-expert relief valve. Three card pickers (target /
// interceptor / scenario flavour) and one Run button. Lands on
// /analyse/runs/:id with a real Monte Carlo run.
//
// Flow:
//   GET /api/sim/quick-sim/catalog            → render the three rows
//   user clicks one card per row              → enable Run button
//   POST /api/sim/quick-sim/compose           → server makes a
//                                               sim_scenarios doc
//                                               flagged is_quick_sim
//   POST /api/runs {scenario_id, seed: 42}    → existing Pass 2 path
//   navigate(`/analyse/runs/:id`)
//
// First-visit tour: a 3-step skippable Dialog overlay shown only on
// the first visit per browser (localStorage flag
// `uavsimnet_quick_sim_seen`). Steps map 1-to-1 to the three rows.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Crosshair, PlayCircle, Plane, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/button";
import SpinnerButton from "@/components/SpinnerButton";
import PageHeader from "@/components/PageHeader";
import { runs as runsApi, sim } from "@/api/endpoints";

const TOUR_STORAGE_KEY = "uavsimnet_quick_sim_seen";
const TOUR_STEPS = [
    {
        title: "Pick a target",
        body:
            "Choose what's flying. From a small consumer quadcopter to a swarm of fixed-wings — the curated set covers the doctrinally interesting threats.",
        icon: Crosshair,
    },
    {
        title: "Pick an interceptor",
        body:
            "Choose what's defending. Each card lists the airframe class so you can match the engagement geometry to the platform.",
        icon: Plane,
    },
    {
        title: "Pick a scenario flavour",
        body:
            "Pick the conditions. Clear weather, contested winds, or an urban canyon — same engine, different decisions.",
        icon: Sparkles,
    },
];

// ---------------------------------------------------------------------
// PickerCard — the visual primitive used by all three rows.
// ---------------------------------------------------------------------
function PickerCard({
    item,
    selected,
    onSelect,
    iconSrc,
    chip,
    testIdPrefix,
}) {
    return (
        <button
            type="button"
            onClick={() => onSelect(item.id)}
            data-testid={`${testIdPrefix}-${item.id}`}
            data-selected={selected ? "true" : "false"}
            className={[
                "flex flex-col items-start gap-3 p-4 rounded-xl border text-left transition-all",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ux-accent",
                selected
                    ? "border-ux-accent bg-ux-accent/5 ring-1 ring-ux-accent shadow-[0_0_0_1px_var(--ux-accent)]"
                    : "border-ux-border-1 bg-ux-bg-1 hover:border-ux-border-2 hover:bg-ux-bg-2",
            ].join(" ")}
        >
            <div
                className={[
                    "h-16 w-16 rounded-lg flex items-center justify-center",
                    selected
                        ? "text-ux-accent bg-ux-accent/10"
                        : "text-ux-text-2 bg-ux-bg-2",
                ].join(" ")}
                aria-hidden
            >
                <img
                    src={iconSrc}
                    alt=""
                    className="h-12 w-12"
                    style={{ filter: "brightness(1.1)" }}
                />
            </div>
            <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-medium text-ux-text-1">
                        {item.name}
                    </span>
                    {chip && (
                        <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-ux-border-1 text-ux-text-3">
                            {chip}
                        </span>
                    )}
                </div>
                <p className="text-[12px] text-ux-text-3 leading-relaxed">
                    {item.description}
                </p>
            </div>
        </button>
    );
}

// ---------------------------------------------------------------------
// Row — one of the three picker rows (target / interceptor / flavour).
// ---------------------------------------------------------------------
function PickerRow({ index, label, hint, items, selected, onSelect, renderCard }) {
    return (
        <section
            className="space-y-3"
            data-testid={`quick-sim-row-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3 font-mono">
                        Step {index + 1}
                    </div>
                    <h2 className="text-[18px] text-ux-text-1 font-medium mt-0.5">
                        {label}
                    </h2>
                    <p className="text-[12px] text-ux-text-3 max-w-md mt-1">
                        {hint}
                    </p>
                </div>
                {selected && (
                    <div className="text-[11px] font-mono text-ux-accent">
                        selected:&nbsp;
                        <span className="text-ux-text-2">
                            {items.find((i) => i.id === selected)?.name || "—"}
                        </span>
                    </div>
                )}
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((item) => renderCard(item))}
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------
// First-visit tour — three steps, skippable, persists to localStorage.
// ---------------------------------------------------------------------
function FirstVisitTour({ open, onClose }) {
    const [step, setStep] = useState(0);
    useEffect(() => {
        if (open) setStep(0);
    }, [open]);
    const total = TOUR_STEPS.length;
    const cur = TOUR_STEPS[step];
    const Icon = cur.icon;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="sm:max-w-[440px] bg-ux-bg-1 border-ux-border-1 text-ux-text-1"
                data-testid="quick-sim-tour"
            >
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div
                            className="h-10 w-10 rounded-lg flex items-center justify-center bg-ux-accent/10 text-ux-accent"
                            aria-hidden
                        >
                            <Icon className="h-5 w-5" />
                        </div>
                        <div>
                            <DialogTitle className="text-[15px] text-ux-text-1">
                                {cur.title}
                            </DialogTitle>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-ux-text-3 font-mono mt-0.5">
                                Step {step + 1} of {total}
                            </div>
                        </div>
                    </div>
                </DialogHeader>
                <DialogDescription className="text-[13px] leading-relaxed text-ux-text-2">
                    {cur.body}
                </DialogDescription>
                <DialogFooter className="flex flex-row items-center justify-between sm:justify-between gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        data-testid="quick-sim-tour-skip"
                        className="text-ux-text-3 hover:text-ux-text-1"
                    >
                        Skip
                    </Button>
                    <div className="flex items-center gap-2">
                        {step > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setStep((s) => Math.max(0, s - 1))}
                                data-testid="quick-sim-tour-back"
                            >
                                Back
                            </Button>
                        )}
                        {step < total - 1 ? (
                            <Button
                                type="button"
                                onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
                                data-testid="quick-sim-tour-next"
                                className="bg-ux-accent text-[#0B0F14] hover:brightness-110"
                            >
                                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={onClose}
                                data-testid="quick-sim-tour-done"
                                className="bg-ux-accent text-[#0B0F14] hover:brightness-110"
                            >
                                Got it
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------
export default function QuickSimPage() {
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [threatId, setThreatId] = useState(null);
    const [interceptorId, setInterceptorId] = useState(null);
    const [flavourId, setFlavourId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);

    // First-visit tour gate.
    useEffect(() => {
        try {
            if (!window.localStorage.getItem(TOUR_STORAGE_KEY)) {
                setTourOpen(true);
            }
        } catch {
            // localStorage disabled — fall through, no tour.
        }
    }, []);

    const closeTour = useCallback(() => {
        setTourOpen(false);
        try {
            window.localStorage.setItem(TOUR_STORAGE_KEY, "1");
        } catch {
            // no-op
        }
    }, []);

    // Fetch the picker catalogue.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cat = await sim.quickSim.catalog();
                if (cancelled) return;
                setCatalog(cat);
                // Sensible default: pre-select the most demo-friendly combo
                // so a confused operator can press Run immediately. Picks
                // are honoured but not forced — every card stays clickable.
                if (cat?.targets?.[1]) setThreatId(cat.targets[1].id);
                if (cat?.interceptors?.[0]) setInterceptorId(cat.interceptors[0].id);
                if (cat?.flavours?.[0]) setFlavourId(cat.flavours[0].id);
            } catch (e) {
                if (!cancelled) {
                    setLoadErr(
                        formatApiError(e, "Failed to load Quick Sim catalogue"),
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const allPicked = useMemo(
        () => !!(threatId && interceptorId && flavourId),
        [threatId, interceptorId, flavourId],
    );

    const handleRun = useCallback(async () => {
        if (!allPicked) return;
        setSubmitting(true);
        try {
            const composed = await sim.quickSim.compose({
                threat_id: threatId,
                interceptor_id: interceptorId,
                flavour_id: flavourId,
            });
            const created = await runsApi.submit({
                scenario_id: composed.scenario_id,
                seed: 42,
                run_type: "monte_carlo",
            });
            navigate(`/analyse/runs/${created.id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.blocking)) {
                toast.error(
                    `Quick Sim composition blocked by ${detail.blocking.length} validation rule${detail.blocking.length === 1 ? "" : "s"}.`,
                );
            } else {
                toast.error(
                    (typeof detail === "string" && detail) ||
                        e?.message ||
                        "Failed to start the Quick Sim run.",
                );
            }
        } finally {
            setSubmitting(false);
        }
    }, [allPicked, flavourId, interceptorId, navigate, threatId]);

    if (loadErr) {
        return (
            <div className="space-y-4" data-testid="quick-sim-page">
                <PageHeader
                    title="Quick Sim"
                    subtitle="Pick a target. Pick an interceptor. Hit Run."
                />
                <div className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/40 p-4 text-ux-danger text-[13px]">
                    {String(loadErr)}
                </div>
            </div>
        );
    }

    if (!catalog) {
        return (
            <div
                className="flex items-center justify-center h-[40vh] text-ux-text-3 font-mono text-[13px]"
                data-testid="quick-sim-loading"
            >
                Loading Quick Sim catalogue…
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="quick-sim-page">
            <PageHeader
                title="Quick Sim"
                subtitle="Three decisions, one Run button. Land on a real Monte Carlo result in under a minute."
            />

            <PickerRow
                index={0}
                label="Target"
                hint="What's flying. Each card maps to a curated threat preset that's been fuze-gate-checked."
                items={catalog.targets}
                selected={threatId}
                onSelect={setThreatId}
                renderCard={(item) => (
                    <PickerCard
                        key={item.id}
                        item={item}
                        selected={threatId === item.id}
                        onSelect={setThreatId}
                        iconSrc={item.silhouette}
                        chip={item.threat_class}
                        testIdPrefix="quick-sim-target"
                    />
                )}
            />

            <PickerRow
                index={1}
                label="Interceptor"
                hint="What's defending. Each interceptor is a verified airframe with calibrated aero + propulsion."
                items={catalog.interceptors}
                selected={interceptorId}
                onSelect={setInterceptorId}
                renderCard={(item) => (
                    <PickerCard
                        key={item.id}
                        item={item}
                        selected={interceptorId === item.id}
                        onSelect={setInterceptorId}
                        iconSrc={item.silhouette}
                        chip={item.airframe_class}
                        testIdPrefix="quick-sim-interceptor"
                    />
                )}
            />

            <PickerRow
                index={2}
                label="Scenario flavour"
                hint="The conditions. Same engine, different decisions — pick the backdrop you care about."
                items={catalog.flavours}
                selected={flavourId}
                onSelect={setFlavourId}
                renderCard={(item) => (
                    <PickerCard
                        key={item.id}
                        item={item}
                        selected={flavourId === item.id}
                        onSelect={setFlavourId}
                        iconSrc={item.glyph}
                        chip={null}
                        testIdPrefix="quick-sim-flavour"
                    />
                )}
            />

            <div
                className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 border-t border-ux-border-1 bg-ux-bg-1/90 backdrop-blur-sm px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4"
                data-testid="quick-sim-actionbar"
            >
                <div className="text-[12px] text-ux-text-3 font-mono">
                    {allPicked
                        ? "Ready. Seed 42, Monte Carlo."
                        : "Pick one card from each row to enable Run."}
                </div>
                <SpinnerButton
                    disabled={!allPicked || submitting}
                    loading={submitting}
                    onClick={handleRun}
                    testId="quick-sim-run-button"
                >
                    <PlayCircle className="w-3.5 h-3.5" />
                    Run
                </SpinnerButton>
            </div>

            <FirstVisitTour open={tourOpen} onClose={closeTour} />
        </div>
    );
}
